import { CommonModule } from '@angular/common';
import { Component, OnInit, signal, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { HeroUserPillComponent } from './hero-user-pill.component';
import { PortalNavbarComponent } from './portal-navbar.component';
import { TeamService, TeamDto, TeamCreateDto, TeamUpdateDto, TeamPlayerDto, TeamPlayerCreateDto, TeamPlayerUpdateDto, EventTeamAssignmentDto, TeamAssignmentDto, TeamPlayerMaintenanceDto } from '../services/team.service';
import { EventService, EventDto } from '../services/event.service';
import { AuthService } from '../services/auth.service';
import { OrganizationDto, OrganizationService } from '../services/organization.service';
import { ManagedUserListItemDto, UserManagementService } from '../services/user-management.service';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

interface TeamRecord {
  id: number;
  organizationId: number;
  name: string;
  eventIds: number[];
  color: string;
  isEnabled: boolean;
  isDeleted: boolean;
}

type TeamLifecycleAction = 'none' | 'disable' | 'restore' | 'delete';
type PlayerLifecycleAction = 'none' | 'disable' | 'restore' | 'delete';

interface EventOption {
  id: number;
  name: string;
}

interface PlayerRecord {
  id: number;
  firstName: string;
  lastName: string;
  userId?: number;
  order: number;
  enableCustomSound: boolean;
  enableCustomVideo: boolean;
  isEnabled?: boolean;
  isDeleted?: boolean;
  hasCustomSoundArtifact?: boolean;
  hasCustomVideoArtifact?: boolean;
}

interface PlayerAssignment {
  id: number;
  playerId: number;
  teamId: number;
  order: number;
  firstName: string;
  lastName: string;
  userId?: number;
  enableCustomSound: boolean;
  enableCustomVideo: boolean;
  isEnabled?: boolean;
  isDeleted?: boolean;
  hasCustomSoundArtifact?: boolean;
  hasCustomVideoArtifact?: boolean;
}

@Component({
  selector: 'app-teams-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, PortalNavbarComponent, HeroUserPillComponent],
  template: `
    <div class="page">
      <app-portal-navbar></app-portal-navbar>
      <!-- Access Denied Section -->
      @if (forbidden) {
        <section class="error-section">
          <h3>Access Denied</h3>
          <p>You do not have access to view teams. Please contact your administrator.</p>
        </section>
      }
      @if (!forbidden) {
      <section>
        <!-- ══════════════════ HERO ══════════════════════════════ -->
        <section class="page-hero">
          <div class="page-hero-inner">
            <a routerLink="/" class="home-btn">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                   viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
              Home
            </a>
            <div class="hero-top-row">
              <div class="hero-icon hero-icon--cyan">
                <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26"
                     viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <app-hero-user-pill></app-hero-user-pill>
            </div>
            <h1 class="page-title">Teams</h1>
            <p class="page-sub">Organize participants into competing teams and assign them to events.</p>
          </div>
        </section>

        <!-- ══════════════════ BODY ═══════════════════════════════ -->
        <section class="page-body">
          <div class="page-body-inner">

            <!-- ── Action bar ─────────────────────────────────── -->
            <div class="action-bar">
              <button class="btn btn--primary" (click)="openNew()">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                     viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5"  y1="12" x2="19" y2="12"/>
                </svg>
                New Team
              </button>
            </div>

            <!-- Teams list -->
            <div class="teams-card">
              <div class="teams-card-hd">
                <span class="teams-card-title">All Teams</span>
                <div class="teams-card-tools">
                  <div class="teams-tool-input-wrap">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12"
                         viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <circle cx="11" cy="11" r="8"/>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                    <input class="teams-tool-input" placeholder="Filter teams…" [(ngModel)]="teamFilterText" />
                  </div>

                  <select class="teams-tool-select" [(ngModel)]="teamSortMode">
                    <option value="newest">Newest</option>
                    <option value="oldest">Oldest</option>
                    <option value="name-asc">Name A-Z</option>
                    <option value="name-desc">Name Z-A</option>
                    <option value="color">Color</option>
                  </select>

                  <span class="teams-count">
                    {{ filteredTeams.length }} team{{ filteredTeams.length !== 1 ? 's' : '' }}
                  </span>
                </div>
              </div>

              <div class="teams-list">
                <div class="teams-col-hd">
                  <span class="col-team-name">Team</span>
                  <span class="col-team-events">Events</span>
                  @if (showOrganizationColumn()) {
                    <span class="col-team-org">Organization</span>
                  }
                  <span class="col-team-color">Color</span>
                  <span class="col-team-edit"></span>
                </div>
                @for (team of filteredTeams; track team.id) {
                  <div class="team-row"
                       (click)="selectTeam(team)"
                       [class.row--selected]="selectedTeam?.id === team.id">
                    <div class="team-dot" [ngClass]="'dot--' + team.color"></div>
                    <div class="team-info">
                      <span class="team-name">{{ team.name }}</span>
                    </div>
                    <span class="team-events">{{ getEventNames(team.eventIds) }}</span>
                    @if (showOrganizationColumn()) {
                      <span class="team-org">{{ getOrganizationName(team.organizationId) }}</span>
                    }
                    <span class="color-chip" [ngClass]="'chip--' + team.color">
                      {{ team.color | titlecase }}
                    </span>
                    <div class="row-chevron" (click)="openEdit(team); $event.stopPropagation()" title="Edit team">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                           viewBox="0 0 24 24" fill="none" stroke="currentColor"
                           stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </div>
                  </div>
                } @empty {
                  <div class="teams-empty">
                    No teams yet — create your first team below.
                  </div>
                }
              </div>
            </div>

            <!-- ── Action bar ─────────────────────────────────── -->
            <div class="action-bar">
              <button class="btn btn--primary" (click)="addPlayer()">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                     viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5"  y1="12" x2="19" y2="12"/>
                </svg>
                New Player
              </button>

              <span class="action-spacer"></span>
            </div>

            <!-- ── Players section ───────────────────────────── -->
            <div class="players-card">
              <div class="players-card-hd">
                <span class="players-card-title">Players</span>
                <span class="players-count">
                  {{ players().length }} player{{ players().length !== 1 ? 's' : '' }}
                </span>
              </div>

              <div class="players-col-hd">
                <span class="col-first">First Name</span>
                <span class="col-last">Last Name</span>
                <span class="col-custom">Custom AV</span>
                <span class="col-del"></span>
              </div>

              <div class="players-list">
                @for (player of players(); track player.id) {
                  <div class="player-row">
                    @if (isOrgLinked(player)) {
                      <button type="button" class="player-input player-input--org player-input--link" (click)="openPlayerMedia(player)" title="Open My Media filtered to this user">
                        {{ player.firstName }}
                      </button>
                    } @else {
                      <input class="player-input" [class.player-input--org]="isOrgLinked(player)" type="text"
                             [(ngModel)]="player.firstName" placeholder="First name"
                             (blur)="savePlayer(player)">
                    }
                    @if (isOrgLinked(player)) {
                      <button type="button" class="player-input player-input--org player-input--link" (click)="openPlayerMedia(player)" title="Open My Media filtered to this user">
                        {{ player.lastName }}
                      </button>
                    } @else {
                      <input class="player-input" [class.player-input--org]="isOrgLinked(player)" type="text"
                             [(ngModel)]="player.lastName" placeholder="Last name"
                             (blur)="savePlayer(player)">
                    }
                    <div class="player-custom-wrap">
                      @if (player.userId) {
                        <button type="button" class="player-kind player-kind--toggle" (click)="revertPlayerToManual(player)" title="Switch back to manual user">
                          System User
                        </button>
                      } @else {
                        <span class="player-kind player-kind--manual">Manual</span>
                      }
                      @if (player.userId) {
                        @if (player.hasCustomSoundArtifact || player.hasCustomVideoArtifact) {
                          <div class="player-media-icons">
                            @if (player.hasCustomSoundArtifact) {
                              <button type="button" class="player-media-box" [class.player-media-box--active]="player.enableCustomSound" (click)="togglePlayerCustomMedia(player, 'sound')" title="Toggle custom sound">
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                  <path d="M9 18V5l12-2v13"/>
                                  <circle cx="6" cy="18" r="3"/>
                                  <circle cx="18" cy="16" r="3"/>
                                </svg>
                              </button>
                            }
                            @if (player.hasCustomVideoArtifact) {
                              <button type="button" class="player-media-box" [class.player-media-box--active]="player.enableCustomVideo" (click)="togglePlayerCustomMedia(player, 'video')" title="Toggle custom video">
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                  <rect x="2" y="3" width="15" height="18" rx="2" ry="2"/>
                                  <polygon points="17 8 22 12 17 16 17 8"/>
                                </svg>
                              </button>
                            }
                          </div>
                        }
                      }
                      @if (!player.userId) {
                        <button class="player-pref-link" (click)="openOrgUserPicker(player)" title="Select organization user">
                          Org User
                        </button>
                      }
                    </div>
                    <button class="player-delete" (click)="onPlayerDeleteClicked(player)" title="Player options">
                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13"
                           viewBox="0 0 24 24" fill="none" stroke="currentColor"
                           stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6"  y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>
                } @empty {
                  <div class="players-empty">
                    No players yet — click New Player to add one.
                  </div>
                }
              </div>
            </div>

            <!-- ── Team Assignments section ──────────────────── -->
            <div class="assignments-card">
              <div class="assignments-card-hd">
                <span class="assignments-card-title">Team Assignments</span>
                <div class="assignments-hd-right">
                  <div class="filter-wrap">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12"
                         viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                    </svg>
                    <select class="filter-select" [(ngModel)]="filterTeamId">
                      <option [ngValue]="0">All Teams</option>
                      @for (team of teams(); track team.id) {
                        <option [ngValue]="team.id">{{ team.name }}</option>
                      }
                    </select>
                  </div>
                  <span class="assignments-count">
                    {{ filteredAssignments.length }} assignment{{ filteredAssignments.length !== 1 ? 's' : '' }}
                  </span>
                </div>
              </div>

              <div class="assignment-create-row">
                <select class="filter-select" [(ngModel)]="assignmentSourcePlayerId">
                  <option [ngValue]="0">Select Player…</option>
                  @for (player of players(); track player.id) {
                    <option [ngValue]="player.id">{{ player.firstName }} {{ player.lastName }}</option>
                  }
                </select>

                <select class="filter-select" [(ngModel)]="assignmentTargetTeamId">
                  <option [ngValue]="0">Select Team…</option>
                  @for (team of teams(); track team.id) {
                    <option [ngValue]="team.id">{{ team.name }}</option>
                  }
                </select>

                <button class="btn btn--primary" (click)="addAssignment()">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                       viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5"  y1="12" x2="19" y2="12"/>
              </svg>
                  New Assignment
                </button>
              </div>

              <div class="assignments-col-hd">
                <span class="col-order">#</span>
                <span class="col-player">Player</span>
                <span class="col-team">Team</span>
                <span class="col-del"></span>
              </div>

              <div class="assignments-list">
                @for (assignment of filteredAssignments; track assignment.id) {
                  <div class="assignment-row"
                       [draggable]="!isAssignmentReorderSaving"
                       [class.assignment-row--dragging]="draggingAssignmentId === assignment.id"
                       [class.assignment-row--over]="dragOverAssignmentId === assignment.id"
                       [class.assignment-row--saving]="isAssignmentReorderSaving"
                       (dragstart)="onAssignmentDragStart($event, assignment.id)"
                       (dragover)="onAssignmentDragOver($event, assignment.id)"
                       (dragleave)="onAssignmentDragLeave($event)"
                       (drop)="onAssignmentDrop($event, assignment.id)"
                       (dragend)="onAssignmentDragEnd()">
                    <span class="assignment-order">{{ assignment.order }}</span>
                    <span class="player-input" [class.player-input--org]="isOrgLinked(assignment)">{{ assignment.firstName }} {{ assignment.lastName }}</span>
                    <span class="player-input">{{ getTeamName(assignment.teamId) }}</span>
                    <button class="player-delete" (click)="deleteAssignment(assignment.id)" title="Remove assignment">
                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13"
                           viewBox="0 0 24 24" fill="none" stroke="currentColor"
                           stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6"  y1="6" x2="18" y2="18"/>
                    </svg>
                    </button>
                  </div>
                } @empty {
                  <div class="assignments-empty">
                    No assignments yet — create one to assign players to teams.
                  </div>
                }
              </div>
            </div>

          </div>
        </section>

        <!-- ══════════════════ MODAL ══════════════════════════════ -->
        @if (isModalOpen) {
          <div class="modal-backdrop" (click)="closeModal()">
            <div class="modal" (click)="$event.stopPropagation()">


              <!-- Header -->
              <div class="modal-hd">
                <h2 class="modal-title">{{ isNewTeam ? 'New Team' : 'Edit Team' }}</h2>
                <button class="modal-close" (click)="closeModal()" aria-label="Close">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                       viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="18" y1="6"  x2="6"  y2="18"/>
                    <line x1="6"  y1="6"  x2="18" y2="18"/>
                  </svg>
                </button>
              </div>

              <!-- Fields -->
              <div class="modal-body">
                <div class="field">
                  <label class="field-label" for="team-name">Team Name</label>
                  <input id="team-name" class="field-input" type="text"
                         [(ngModel)]="editDraft.name"
                         placeholder="Enter team name">
                </div>

                <div class="field">
                  <label class="field-label" for="team-event">Assigned Events</label>
                  <select id="team-event" class="field-select"
                          multiple
                          [(ngModel)]="editDraft.eventIds"
                          (ngModelChange)="onEditEventSelectionChanged($event)">
                    <option [ngValue]="0">Unassigned</option>
                    @for (evt of eventOptions; track evt.id) {
                      <option [ngValue]="evt.id">{{ evt.name }}</option>
                    }
                  </select>
                </div>

                <div class="field">
                  <label class="field-label" for="team-color">Team Color</label>
                  <select id="team-color" class="field-select"
                          [(ngModel)]="editDraft.color">
                    @for (c of colorOptions; track c.value) {
                      <option [value]="c.value">{{ c.label }}</option>
                    }
                  </select>
                  <div class="color-preview">
                    <div class="color-swatch" [ngClass]="'swatch--' + editDraft.color"></div>
                    <span class="color-preview-label">{{ editDraft.color | titlecase }}</span>
                  </div>
                </div>
              </div>

              <!-- Footer -->
              <div class="modal-ft" [class.modal-ft--end]="isNewTeam">
                @if (!isNewTeam) {
                  <div class="modal-actions">
                    @if (canManageTeamLifecycle && !editDraft.isDeleted) {
                      @if (editDraft.isEnabled) {
                        <button
                          class="btn btn--lifecycle-blue"
                          [class.btn--lifecycle-blue-active]="isTeamLifecycleActionSelected('disable')"
                          (click)="selectTeamLifecycleAction('disable')">
                          Disable
                        </button>
                      } @else {
                        <button
                          class="btn btn--lifecycle-blue"
                          [class.btn--lifecycle-blue-active]="isTeamLifecycleActionSelected('restore')"
                          (click)="selectTeamLifecycleAction('restore')">
                          Restore
                        </button>
                      }
                      <button
                        class="btn btn--danger"
                        [class.btn--danger-active]="isTeamLifecycleActionSelected('delete')"
                        (click)="selectTeamLifecycleAction('delete')">
                        Delete
                      </button>
                    }
                    @if (canRestoreDeletedTeams && editDraft.isDeleted) {
                      <button
                        class="btn btn--lifecycle-blue"
                        [class.btn--lifecycle-blue-active]="isTeamLifecycleActionSelected('restore')"
                        (click)="selectTeamLifecycleAction('restore')">
                        Restore
                      </button>
                    }
                  </div>
                }
                <div class="modal-actions">
                  <button class="btn btn--ghost" (click)="closeModal()">Cancel</button>
                  <button class="btn btn--primary" [disabled]="editDraft.isDeleted" (click)="saveTeam()">OK</button>
                </div>
              </div>

            </div>
          </div>
        }

        @if (isOrgUserPickerOpen) {
          <div class="modal-backdrop" (click)="closeOrgUserPicker()">
            <div class="modal" (click)="$event.stopPropagation()">
              <div class="modal-hd">
                <h2 class="modal-title">Select Organization User</h2>
                <button class="modal-close" (click)="closeOrgUserPicker()" aria-label="Close">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                       viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>

              <div class="modal-body">
                <input class="field-input" type="text" placeholder="Filter users…" [(ngModel)]="orgUserFilterText" />

                @if (orgUsersLoading) {
                  <p class="picker-empty">Loading organization users…</p>
                } @else {
                  <div class="org-user-list">
                    @for (user of filteredOrgUsers; track user.userId + '-' + user.organizationId + '-' + user.role) {
                      <button class="org-user-row" type="button" (click)="selectOrgUserForPlayer(user)">
                        <span class="org-user-name">{{ getManagedUserDisplayName(user) }}</span>
                        <span class="org-user-meta">{{ user.email }}</span>
                        <span class="org-user-meta">{{ user.role }}</span>
                      </button>
                    } @empty {
                      <div class="picker-empty">No users found for this organization.</div>
                    }
                  </div>
                }
              </div>
            </div>
          </div>
        }

        @if (isPlayerModalOpen && playerModalDraft) {
          <div class="modal-backdrop" (click)="closePlayerModal()">
            <div class="modal" (click)="$event.stopPropagation()">
              <div class="modal-hd">
                <h2 class="modal-title">Player Details</h2>
                <button class="modal-close" (click)="closePlayerModal()" aria-label="Close">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                       viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>

              <div class="modal-body">
                <div class="field">
                  <label class="field-label">First Name</label>
                  <input class="field-input" type="text" [ngModel]="playerModalDraft.firstName" readonly>
                </div>
                <div class="field">
                  <label class="field-label">Last Name</label>
                  <input class="field-input" type="text" [ngModel]="playerModalDraft.lastName" readonly>
                </div>
                <div class="field">
                  <label class="field-label">Player Type</label>
                  <input class="field-input" type="text" [ngModel]="playerModalDraft.userId ? 'System User' : 'Manual User'" readonly>
                </div>
                <div class="field">
                  <label class="field-label">Status</label>
                  <input class="field-input" type="text" [ngModel]="playerModalDraft.isDeleted ? 'Deleted' : (playerModalDraft.isEnabled ? 'Enabled' : 'Disabled')" readonly>
                </div>
              </div>

              <div class="modal-ft">
                <div class="modal-actions">
                  @if (canManagePlayerLifecycle && !playerModalDraft.isDeleted) {
                    @if (playerModalDraft.isEnabled) {
                      <button
                        class="btn btn--lifecycle-blue"
                        [class.btn--lifecycle-blue-active]="isPlayerLifecycleActionSelected('disable')"
                        (click)="selectPlayerLifecycleAction('disable')">
                        Disable
                      </button>
                    } @else {
                      <button
                        class="btn btn--lifecycle-blue"
                        [class.btn--lifecycle-blue-active]="isPlayerLifecycleActionSelected('restore')"
                        (click)="selectPlayerLifecycleAction('restore')">
                        Restore
                      </button>
                    }
                    <button
                      class="btn btn--danger"
                      [class.btn--danger-active]="isPlayerLifecycleActionSelected('delete')"
                      (click)="selectPlayerLifecycleAction('delete')">
                      Delete
                    </button>
                  }
                  @if (canRestoreDeletedPlayers && playerModalDraft.isDeleted) {
                    <button
                      class="btn btn--lifecycle-blue"
                      [class.btn--lifecycle-blue-active]="isPlayerLifecycleActionSelected('restore')"
                      (click)="selectPlayerLifecycleAction('restore')">
                      Restore
                    </button>
                  }
                </div>
                <div class="modal-actions">
                  <button class="btn btn--ghost" (click)="closePlayerModal()">Cancel</button>
                  <button class="btn btn--primary" [disabled]="pendingPlayerLifecycleAction === 'none'" (click)="confirmPlayerModalLifecycleAction()">OK</button>
                </div>
              </div>
            </div>
          </div>
        }

      </section>
      }
    </div>
  `,
  styles: [`
    /* ── Page shell ─────────────────────────────────────────── */
    .page {
      display: flex;
      flex-direction: column;
      min-height: 100vh;
    }

    /* ── Hero ───────────────────────────────────────────────── */
    .page-hero {
      background:
        radial-gradient(
          ellipse at 12% 60%,
          color-mix(in srgb, #06b6d4 8%, transparent) 0%,
          transparent 55%
        ),
        linear-gradient(155deg, var(--color-bg-start) 0%, var(--color-bg-end) 100%);
      border-bottom: 1px solid var(--color-border);
      padding: 3rem 1.5rem 3.5rem;
    }

    .page-hero-inner {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      margin: 0 auto;
      max-width: 1280px;
    }

    .home-btn {
      align-items: center;
      align-self: flex-start;
      background: color-mix(in srgb, var(--color-surface-2) 65%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 0.5rem;
      color: var(--color-muted);
      display: flex;
      font-size: 0.8rem;
      font-weight: 500;
      gap: 0.35rem;
      padding: 0.32rem 0.7rem;
      text-decoration: none;
      transition: background 0.15s, color 0.15s;
    }

    .home-btn:hover {
      background: color-mix(in srgb, var(--color-surface-2) 90%, transparent);
      color: var(--color-text);
    }

    .hero-top-row {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      gap: 0.85rem;
    }

    .hero-icon {
      align-items: center;
      border-radius: 0.75rem;
      display: flex;
      height: 52px;
      justify-content: center;
      width: 52px;
    }

    .hero-icon--cyan {
      background: color-mix(in srgb, #06b6d4 16%, transparent);
      color: #22d3ee;
    }

    .page-title {
      color: var(--color-text);
      font-size: clamp(1.6rem, 3vw, 2.2rem);
      font-weight: 800;
      line-height: 1.15;
      margin: 0;
    }

    .page-sub {
      color: var(--color-muted);
      font-size: 0.93rem;
      line-height: 1.6;
      margin: 0;
      max-width: 52ch;
    }

    /* ── Body ───────────────────────────────────────────────── */
    .page-body {
      flex: 1;
      padding: 2.5rem 1.5rem;
    }

    .page-body-inner {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
      margin: 0 auto;
      max-width: 1280px;
    }

    /* ── Teams card ─────────────────────────────────────────── */
    .teams-card {
      background: color-mix(in srgb, var(--color-surface-2) 75%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 1rem;
      overflow: hidden;
    }

    .teams-card-hd {
      align-items: center;
      border-bottom: 1px solid var(--color-border);
      display: flex;
      justify-content: space-between;
      gap: 0.65rem;
      flex-wrap: wrap;
      padding: 0.85rem 1.25rem;
    }

    .teams-card-title {
      color: var(--color-text);
      font-size: 0.88rem;
      font-weight: 700;
    }

    .teams-count {
      color: var(--color-muted);
      font-size: 0.75rem;
    }

    .teams-card-tools {
      display: flex;
      align-items: center;
      gap: 0.55rem;
      flex-wrap: wrap;
      margin-left: auto;
    }

    .teams-tool-input-wrap {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      border: 1px solid var(--color-border);
      border-radius: 0.55rem;
      background: color-mix(in srgb, var(--color-surface-2) 72%, transparent);
      padding: 0.38rem 0.55rem;
      color: var(--color-muted);
    }

    .teams-tool-input {
      border: none;
      outline: none;
      background: transparent;
      color: var(--color-text);
      min-width: 180px;
      font-size: 0.82rem;
    }

    .teams-tool-input::placeholder {
      color: var(--color-muted);
    }

    .teams-tool-select {
      border: 1px solid var(--color-border);
      border-radius: 0.55rem;
      background: color-mix(in srgb, var(--color-surface-2) 72%, transparent);
      color: var(--color-text);
      font-size: 0.8rem;
      padding: 0.38rem 0.55rem;
    }

    .teams-list { padding: 0.2rem 0; }

    .teams-col-hd {
      color: var(--color-muted);
      display: grid;
      gap: 1rem;
      grid-template-columns: 22px minmax(140px, 1fr) minmax(170px, 1.3fr) minmax(170px, 180px) 54px 28px;
      font-size: 0.72rem;
      font-weight: 600;
      letter-spacing: 0.02em;
      padding: 0.45rem 1.25rem 0.35rem;
      text-transform: uppercase;
    }

    .teams-col-hd .col-team-name { grid-column: 2; }
    .teams-col-hd .col-team-events { grid-column: 3; }
    .teams-col-hd .col-team-org { grid-column: 4; }
    .teams-col-hd .col-team-color { grid-column: 5; justify-self: center; }
    .teams-col-hd .col-team-edit { grid-column: 6; }

    .team-row {
      align-items: center;
      border-bottom: 1px solid color-mix(in srgb, var(--color-border) 45%, transparent);
      cursor: pointer;
      display: grid;
      gap: 1rem;
      grid-template-columns: 22px minmax(140px, 1fr) minmax(170px, 1.3fr) minmax(170px, 180px) 54px 28px;
      padding: 0.85rem 1.25rem;
      transition: background 0.15s;
    }

    .team-row:not(:has(.team-org)) {
      grid-template-columns: 22px minmax(140px, 1fr) minmax(220px, 1.6fr) 54px 28px;
    }

    .teams-col-hd:not(:has(.col-team-org)) {
      grid-template-columns: 22px minmax(140px, 1fr) minmax(220px, 1.6fr) 54px 28px;
    }

    .teams-col-hd:not(:has(.col-team-org)) .col-team-color { grid-column: 4; }
    .teams-col-hd:not(:has(.col-team-org)) .col-team-edit { grid-column: 5; }

    .team-row:last-child { border-bottom: none; }

    .team-row:hover {
      background: color-mix(in srgb, #ef4444 5%, transparent);
    }

    .team-row:hover .row-chevron {
      color: var(--color-accent-soft);
      opacity: 1;
    }

    .team-dot {
      border-radius: 50%;
      flex-shrink: 0;
      grid-column: 1;
      height: 12px;
      width: 12px;
    }

    .dot--blue   { background: #60a5fa; }
    .dot--cyan   { background: #22d3ee; }
    .dot--green  { background: #4ade80; }
    .dot--yellow { background: #fbbf24; }
    .dot--red    { background: #f87171; }
    .dot--purple { background: #a78bfa; }
    .dot--orange { background: #fb923c; }

    .team-info {
      display: flex;
      flex-direction: column;
      gap: 0.18rem;
      grid-column: 2;
      min-width: 0;
    }

    .team-events {
      color: var(--color-muted);
      font-size: 0.75rem;
      grid-column: 3;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .team-org {
      color: var(--color-muted);
      font-size: 0.75rem;
      grid-column: 4;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .team-name {
      color: var(--color-text);
      font-size: 0.88rem;
      font-weight: 600;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .team-meta {
      color: var(--color-muted);
      font-size: 0.75rem;
    }

    .color-chip {
      border-radius: 9999px;
      font-size: 0.64rem;
      font-weight: 600;
      grid-column: 5;
      justify-self: center;
      padding: 0.18rem 0.55rem;
      text-align: center;
      width: 54px;
    }

    .team-row:not(:has(.team-org)) .team-events { grid-column: 3; }
    .team-row:not(:has(.team-org)) .color-chip { grid-column: 4; }
    .team-row:not(:has(.team-org)) .row-chevron { grid-column: 5; }

    .chip--blue   { background: color-mix(in srgb, #60a5fa 16%, transparent); color: #60a5fa; }
    .chip--cyan   { background: color-mix(in srgb, #22d3ee 16%, transparent); color: #22d3ee; }
    .chip--green  { background: color-mix(in srgb, #4ade80 16%, transparent); color: #4ade80; }
    .chip--yellow { background: color-mix(in srgb, #fbbf24 16%, transparent); color: #fbbf24; }
    .chip--red    { background: color-mix(in srgb, #f87171 16%, transparent); color: #f87171; }
    .chip--purple { background: color-mix(in srgb, #a78bfa 16%, transparent); color: #a78bfa; }
    .chip--orange { background: color-mix(in srgb, #fb923c 16%, transparent); color: #fb923c; }

    .row-chevron {
      color: var(--color-muted);
      flex-shrink: 0;
      opacity: 0.35;
      transition: color 0.15s, opacity 0.15s;
    }

    .teams-empty {
      color: var(--color-muted);
      font-size: 0.83rem;
      padding: 2.5rem;
      text-align: center;
    }

    /* ── Action bar ─────────────────────────────────────────── */
    .action-bar {
      display: flex;
      gap: 0.75rem;
      padding-top: 0.25rem;
    }

    .action-spacer { flex: 1; }

    /* ── Buttons ────────────────────────────────────────────── */
    .btn {
      align-items: center;
      border: 1px solid transparent;
      border-radius: 0.5rem;
      cursor: pointer;
      display: inline-flex;
      font-size: 0.83rem;
      font-weight: 600;
      gap: 0.4rem;
      padding: 0.5rem 1rem;
      transition: background 0.15s, border-color 0.15s, color 0.15s;
    }

    .btn--primary { background: #06b6d4; color: #fff; }
    .btn--primary:hover { background: color-mix(in srgb, #06b6d4 82%, #fff); }

    .btn--ghost {
      background: color-mix(in srgb, var(--color-surface-2) 75%, transparent);
      border-color: var(--color-border);
      color: var(--color-muted);
    }
    .btn--ghost:hover { color: var(--color-text); }

    .btn--danger {
      background: color-mix(in srgb, #ef4444 16%, transparent);
      border-color: color-mix(in srgb, #ef4444 28%, transparent);
      color: #f87171;
    }
    .btn--danger:hover {
      background: color-mix(in srgb, #ef4444 26%, transparent);
      border-color: color-mix(in srgb, #ef4444 45%, transparent);
    }

    .btn--lifecycle-blue {
      background: color-mix(in srgb, #38bdf8 20%, transparent);
      border-color: color-mix(in srgb, #38bdf8 35%, transparent);
      color: #7dd3fc;
    }

    .btn--lifecycle-blue:hover {
      background: color-mix(in srgb, #38bdf8 30%, transparent);
      border-color: color-mix(in srgb, #38bdf8 52%, transparent);
      color: #bae6fd;
    }

    .btn--lifecycle-blue-active {
      background: color-mix(in srgb, #38bdf8 42%, transparent);
      border-color: color-mix(in srgb, #38bdf8 70%, transparent);
      box-shadow: 0 0 0 1px color-mix(in srgb, #38bdf8 28%, transparent), 0 0 18px color-mix(in srgb, #38bdf8 40%, transparent);
      color: #e0f2fe;
    }

    .btn--danger-active {
      background: color-mix(in srgb, #ef4444 34%, transparent);
      border-color: color-mix(in srgb, #ef4444 62%, transparent);
      box-shadow: 0 0 0 1px color-mix(in srgb, #ef4444 26%, transparent), 0 0 18px color-mix(in srgb, #ef4444 38%, transparent);
      color: #fecaca;
    }

    /* ── Modal ──────────────────────────────────────────────── */
    .modal-backdrop {
      align-items: center;
      background: rgba(0, 0, 0, 0.65);
      backdrop-filter: blur(4px);
      display: flex;
      inset: 0;
      justify-content: center;
      padding: 1.5rem;
      position: fixed;
      z-index: 1000;
    }

    .modal {
      background: var(--color-bg-end);
      border: 1px solid var(--color-border);
      border-radius: 1rem;
      box-shadow: 0 24px 64px rgba(0, 0, 0, 0.45);
      max-width: 480px;
      width: 100%;
    }

    .modal-hd {
      align-items: center;
      border-bottom: 1px solid var(--color-border);
      display: flex;
      justify-content: space-between;
      padding: 1.1rem 1.4rem;
    }

    .modal-title { color: var(--color-text); font-size: 1rem; font-weight: 700; margin: 0; }

    .modal-close {
      align-items: center;
      background: none;
      border: none;
      border-radius: 0.4rem;
      color: var(--color-muted);
      cursor: pointer;
      display: flex;
      padding: 0.25rem;
      transition: color 0.15s;
    }
    .modal-close:hover { color: var(--color-text); }

    .modal-body { display: flex; flex-direction: column; gap: 1.1rem; padding: 1.4rem; }

    .modal-ft {
      align-items: center;
      border-top: 1px solid var(--color-border);
      display: flex;
      justify-content: space-between;
      padding: 1rem 1.4rem;
    }
    .modal-ft--end { justify-content: flex-end; }
    .modal-actions { display: flex; gap: 0.5rem; }

    /* ── Form fields ────────────────────────────────────────── */
    .field { display: flex; flex-direction: column; gap: 0.4rem; }

    .field-label {
      color: var(--color-muted);
      font-size: 0.72rem;
      font-weight: 600;
      letter-spacing: 0.07em;
      text-transform: uppercase;
    }

    .field-input,
    .field-select {
      background: color-mix(in srgb, var(--color-surface-2) 65%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 0.5rem;
      box-sizing: border-box;
      color: var(--color-text);
      color-scheme: dark;
      font-size: 0.88rem;
      padding: 0.52rem 0.75rem;
      transition: border-color 0.15s;
      width: 100%;
    }
    .field-input:focus, .field-select:focus { border-color: #06b6d4; outline: none; }
    .field-input::placeholder { color: var(--color-muted); }
    .field-select option { background: var(--color-bg-end); color: var(--color-text); }

    /* ── Color preview ──────────────────────────────────────── */
    .color-preview { align-items: center; display: flex; gap: 0.5rem; margin-top: 0.25rem; }
    .color-swatch { border-radius: 50%; height: 14px; width: 14px; }

    .swatch--blue   { background: #60a5fa; }
    .swatch--cyan   { background: #22d3ee; }
    .swatch--green  { background: #4ade80; }
    .swatch--yellow { background: #fbbf24; }
    .swatch--red    { background: #f87171; }
    .swatch--purple { background: #a78bfa; }
    .swatch--orange { background: #fb923c; }

    .color-preview-label { color: var(--color-muted); font-size: 0.75rem; }

    /* ── Players section ────────────────────────────────────── */
    .players-card {
      background: color-mix(in srgb, var(--color-surface-2) 75%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 1rem;
      overflow: hidden;
    }

    .players-card-hd {
      align-items: center;
      border-bottom: 1px solid var(--color-border);
      display: flex;
      justify-content: space-between;
      padding: 0.85rem 1.25rem;
    }

    .players-hd-right {
      align-items: center;
      display: flex;
      gap: 0.55rem;
    }

    .players-card-title { color: var(--color-text); font-size: 0.88rem; font-weight: 700; }
    .players-count { color: var(--color-muted); font-size: 0.75rem; }

    .btn--org-users {
      background: color-mix(in srgb, #8b5cf6 22%, transparent);
      border-color: color-mix(in srgb, #8b5cf6 45%, transparent);
      color: #c4b5fd;
    }
    .btn--org-users:hover {
      background: color-mix(in srgb, #8b5cf6 34%, transparent);
      border-color: color-mix(in srgb, #8b5cf6 62%, transparent);
    }

    .players-col-hd {
      border-bottom: 1px solid var(--color-border);
      color: var(--color-muted);
      display: grid;
      font-size: 0.7rem;
      font-weight: 600;
      gap: 0.5rem;
      grid-template-columns: 1fr 1fr 100px 32px;
      letter-spacing: 0.06em;
      padding: 0.45rem 1.25rem;
      text-transform: uppercase;
    }

    .col-first { text-align: left; }
    .col-last { text-align: left; }
    .col-custom { text-align: center; }
    .col-del { flex-shrink: 0; }

    .players-list { padding: 0.2rem 0; }

    .player-row {
      align-items: center;
      border-bottom: 1px solid color-mix(in srgb, var(--color-border) 40%, transparent);
      display: grid;
      gap: 0.5rem;
      grid-template-columns: 1fr 1fr 100px 28px;
      padding: 0.35rem 1.25rem;
    }
    .player-row:last-child { border-bottom: none; }

    .player-input {
      background: color-mix(in srgb, var(--color-surface-2) 65%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 0.35rem;
      color: var(--color-text);
      font-size: 0.8rem;
      padding: 0.35rem 0.5rem;
      transition: border-color 0.15s;
    }

    .player-input--org {
      background: #06b6d4;
      border-color: color-mix(in srgb, #06b6d4 70%, #fff 30%);
      color: #ffffff;
      font-weight: 600;
    }

    .player-input--org::placeholder {
      color: color-mix(in srgb, #ffffff 70%, transparent);
    }

    .player-input:focus {
      border-color: #06b6d4;
      outline: none;
    }
    .player-input--order { text-align: center; }
    .player-input::placeholder { color: var(--color-muted); opacity: 0.6; }
    .player-input--link {
      cursor: pointer;
      text-align: left;
      text-decoration: underline;
      text-decoration-thickness: 1px;
      text-underline-offset: 2px;
    }
    .player-input--link:hover {
      filter: brightness(1.08);
    }
    .player-select { cursor: pointer; }
    .player-select option { background: var(--color-bg-end); color: var(--color-text); }

    .player-custom-wrap {
      align-items: center;
      display: flex;
      gap: 0.55rem;
      justify-content: center;
      flex-wrap: wrap;
    }

    .player-kind {
      background: color-mix(in srgb, #22d3ee 18%, transparent);
      border: 1px solid color-mix(in srgb, #22d3ee 42%, var(--color-border));
      border-radius: 9999px;
      color: #67e8f9;
      font-size: 0.58rem;
      font-weight: 700;
      letter-spacing: 0.04em;
      padding: 0.08rem 0.38rem;
      text-transform: uppercase;
    }

    .player-kind--toggle {
      cursor: pointer;
    }

    .player-kind--manual {
      background: color-mix(in srgb, var(--color-surface-2) 70%, transparent);
      border-color: var(--color-border);
      color: var(--color-muted);
    }

    .player-media-icons {
      align-items: center;
      display: inline-flex;
      gap: 0.3rem;
    }

    .player-media-box {
      align-items: center;
      background: color-mix(in srgb, var(--color-surface-2) 72%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 0.35rem;
      color: var(--color-muted);
      cursor: pointer;
      display: inline-flex;
      height: 22px;
      justify-content: center;
      width: 22px;
    }

    .player-media-box:hover {
      border-color: color-mix(in srgb, #06b6d4 40%, var(--color-border));
    }

    .player-media-box--active {
      background: color-mix(in srgb, #06b6d4 16%, transparent);
      border-color: color-mix(in srgb, #06b6d4 42%, var(--color-border));
      color: #67e8f9;
    }

    .player-pref-link {
      background: color-mix(in srgb, #8b5cf6 18%, transparent);
      border: 1px solid color-mix(in srgb, #8b5cf6 45%, var(--color-border));
      border-radius: 0.4rem;
      color: #c4b5fd;
      cursor: pointer;
      font-size: 0.62rem;
      font-weight: 600;
      padding: 0.12rem 0.38rem;
    }

    .player-pref-link--muted {
      background: color-mix(in srgb, var(--color-surface-2) 72%, transparent);
      border-color: var(--color-border);
      color: var(--color-muted);
    }

    .org-user-list {
      border: 1px solid var(--color-border);
      border-radius: 0.6rem;
      max-height: 280px;
      overflow: auto;
    }

    .org-user-row {
      align-items: flex-start;
      background: transparent;
      border: 0;
      border-bottom: 1px solid color-mix(in srgb, var(--color-border) 45%, transparent);
      color: inherit;
      cursor: pointer;
      display: grid;
      gap: 0.2rem;
      padding: 0.55rem 0.75rem;
      text-align: left;
      width: 100%;
    }
    .org-user-row:last-child { border-bottom: none; }
    .org-user-row:hover {
      background: color-mix(in srgb, #8b5cf6 8%, transparent);
    }

    .org-user-name {
      color: var(--color-text);
      font-size: 0.8rem;
      font-weight: 600;
    }

    .org-user-meta {
      color: var(--color-muted);
      font-size: 0.72rem;
    }

    .picker-empty {
      color: var(--color-muted);
      font-size: 0.82rem;
      margin: 0;
      padding: 0.5rem 0.1rem;
      text-align: center;
    }

    .player-delete {
      align-items: center;
      background: none;
      border: none;
      border-radius: 0.35rem;
      color: var(--color-muted);
      cursor: pointer;
      display: flex;
      justify-content: center;
      opacity: 0.35;
      padding: 0.25rem;
      transition: background 0.15s, color 0.15s, opacity 0.15s;
    }
    .player-delete:hover {
      background: color-mix(in srgb, #ef4444 16%, transparent);
      color: #f87171;
      opacity: 1;
    }

    .players-empty { color: var(--color-muted); font-size: 0.83rem; padding: 2rem; text-align: center; }

    /* ── Assignments section ───────────────────────────────── */
    .assignments-card {
      background: color-mix(in srgb, var(--color-surface-2) 75%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 1rem;
      overflow: hidden;
    }

    .assignments-card-hd {
      align-items: center;
      border-bottom: 1px solid var(--color-border);
      display: flex;
      justify-content: space-between;
      padding: 0.85rem 1.25rem;
    }

    .assignments-card-title { color: var(--color-text); font-size: 0.88rem; font-weight: 700; }
    .assignments-count { color: var(--color-muted); font-size: 0.75rem; }

    .assignments-hd-right { align-items: center; display: flex; gap: 0.75rem; }

    .filter-wrap {
      align-items: center;
      color: #94a3b8;
      display: flex;
      flex-shrink: 0;
      gap: 0.3rem;
    }

    .filter-select {
      appearance: auto;
      background: #0f172a;
      border: 1px solid #06b6d4;
      border-radius: 0.4rem;
      color: #f8fafc;
      color-scheme: dark;
      cursor: pointer;
      flex-shrink: 0;
      font-size: 0.72rem;
      font-weight: 500;
      padding: 0.2rem 0.5rem;
      transition: border-color 0.15s;
    }
    .filter-select:hover { border-color: #22d3ee; }
    .filter-select:focus { border-color: #22d3ee; outline: none; }
    .filter-select option { background: #0f172a; color: #f8fafc; }

    .assignments-col-hd {
      border-bottom: 1px solid var(--color-border);
      color: var(--color-muted);
      display: grid;
      font-size: 0.7rem;
      font-weight: 600;
      gap: 0.5rem;
      grid-template-columns: 40px 1fr 1fr 32px;
      letter-spacing: 0.06em;
      padding: 0.45rem 1.25rem;
      text-transform: uppercase;
    }

    .assignment-create-row {
      align-items: center;
      border-bottom: 1px solid var(--color-border);
      display: flex;
      gap: 0.6rem;
      padding: 0.65rem 1.25rem;
    }

    .col-order { text-align: center; }
    .col-player { text-align: left; }
    .col-team { text-align: left; }
    .col-del { flex-shrink: 0; }

    .assignments-list { padding: 0.2rem 0; }

    .assignment-row {
      align-items: center;
      border-bottom: 1px solid color-mix(in srgb, var(--color-border) 40%, transparent);
      cursor: grab;
      display: grid;
      gap: 0.5rem;
      grid-template-columns: 40px 1fr 1fr 28px;
      padding: 0.35rem 1.25rem;
    }
    .assignment-row:last-child { border-bottom: none; }

    .assignment-row--dragging {
      opacity: 0.55;
    }

    .assignment-row--over {
      background: color-mix(in srgb, #06b6d4 10%, transparent);
      border-color: color-mix(in srgb, #06b6d4 45%, transparent);
    }

    .assignment-order {
      align-items: center;
      border: 1px solid var(--color-border);
      border-radius: 0.35rem;
      color: var(--color-muted);
      display: inline-flex;
      font-size: 0.75rem;
      font-weight: 600;
      height: 28px;
      justify-content: center;
      width: 28px;
    }

    .assignments-empty { color: var(--color-muted); font-size: 0.83rem; padding: 2rem; text-align: center; }

    @media (max-width: 700px) {
      .assignments-col-hd,
      .assignment-row { grid-template-columns: 40px 1fr 32px; }
      .assignments-col-hd .col-team,
      .assignment-row :nth-child(3) { display: none; }
    }

    .error-section {
      color: #b91c1c;
      background: #fef2f2;
      border: 1px solid #fca5a5;
      padding: 1rem;
      border-radius: 6px;
      margin: 2rem 0;
    }
  `]
})
export class TeamsPageComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly teamService = inject(TeamService);
  private readonly eventService = inject(EventService);
  private readonly organizationService = inject(OrganizationService);
  private readonly userManagementService = inject(UserManagementService);
  private readonly router = inject(Router);

  private currentOrganizationId: number | null = null;
  private isSiteAdminUser = false;
  private canResolveOrganizationLabelsById = false;
  private eventTeamAssignmentsByTeamId: Record<number, EventTeamAssignmentDto[]> = {};
  private readonly organizationNamesById = new Map<number, string>();

  readonly teams = signal<TeamRecord[]>([]);
  readonly players = signal<PlayerRecord[]>([]);
  readonly assignments = signal<PlayerAssignment[]>([]);
  readonly showOrganizationColumn = signal(false);
  private readonly localPlayers = signal<PlayerRecord[]>([]);

  filterTeamId = 0;
  teamFilterText = '';
  teamSortMode: 'newest' | 'oldest' | 'name-asc' | 'name-desc' | 'color' = 'newest';
  assignmentSourcePlayerId = 0;
  assignmentTargetTeamId = 0;

  isOrgUserPickerOpen = false;
  orgUsersLoading = false;
  orgUsers: ManagedUserListItemDto[] = [];
  orgUserFilterText = '';
  private activePlayerForOrgUser: PlayerRecord | null = null;
  draggingAssignmentId: number | null = null;
  dragOverAssignmentId: number | null = null;
  isAssignmentReorderSaving = false;
  canManageTeamLifecycle = false;
  canRestoreDeletedTeams = false;
  canHardDeleteTeams = false;
  canManagePlayerLifecycle = false;
  canRestoreDeletedPlayers = false;
  canHardDeletePlayers = false;
  isPlayerModalOpen = false;
  playerModalDraft: PlayerRecord | null = null;
  pendingPlayerLifecycleAction: PlayerLifecycleAction = 'none';

  forbidden = false; // Add forbidden property for access control

  get filteredOrgUsers(): ManagedUserListItemDto[] {
    const query = this.orgUserFilterText.trim().toLowerCase();
    if (!query) {
      return this.orgUsers;
    }

    return this.orgUsers.filter(user =>
      this.getManagedUserDisplayName(user).toLowerCase().includes(query)
      || user.email.toLowerCase().includes(query)
      || user.role.toLowerCase().includes(query));
  }

  get filteredAssignments(): PlayerAssignment[] {
    const filtered = this.filterTeamId === 0
      ? this.assignments()
      : this.assignments().filter(a => a.teamId === this.filterTeamId);

    return [...filtered].sort((a, b) => a.teamId - b.teamId || a.order - b.order || a.id - b.id);
  }

  get filteredTeams(): TeamRecord[] {
    const query = this.teamFilterText.trim().toLowerCase();
    const filtered = !query
      ? [...this.teams()]
      : this.teams().filter(team =>
          team.name.toLowerCase().includes(query) ||
          this.getOrganizationName(team.organizationId).toLowerCase().includes(query) ||
          `org ${team.organizationId}`.includes(query) ||
          team.color.toLowerCase().includes(query) ||
          this.getEventNames(team.eventIds).toLowerCase().includes(query)
        );

    return filtered.sort((a, b) => {
      if (this.teamSortMode === 'name-asc') return a.name.localeCompare(b.name);
      if (this.teamSortMode === 'name-desc') return b.name.localeCompare(a.name);
      if (this.teamSortMode === 'color') return a.color.localeCompare(b.color) || a.name.localeCompare(b.name);
      return this.teamSortMode === 'oldest' ? a.id - b.id : b.id - a.id;
    });
  }

  // ── Event options ───────────────────────────────────────────────────────
  eventOptions: EventOption[] = [];

  colorOptions = [
    { value: 'blue',   label: 'Blue'   },
    { value: 'cyan',   label: 'Cyan'   },
    { value: 'green',  label: 'Green'  },
    { value: 'yellow', label: 'Yellow' },
    { value: 'red',    label: 'Red'    },
    { value: 'purple', label: 'Purple' },
    { value: 'orange', label: 'Orange' },
  ];

  // ── Modal state ────────────────────────────────────────────────────────
  isModalOpen = false;
  isNewTeam   = false;
  selectedTeam: TeamRecord | null = null;
  pendingTeamLifecycleAction: TeamLifecycleAction = 'none';

  editDraft: TeamRecord = { id: 0, organizationId: 0, name: '', eventIds: [], color: 'blue', isEnabled: true, isDeleted: false };

  private nextPlayerId     = 5;
  private nextAssignmentId = 7;

  ngOnInit(): void {
    void this.initializeRoleContext();
    this.loadOrganizationContext();
    this.loadTeams();
    this.loadEvents();
  }

  private async initializeRoleContext(): Promise<void> {
    const [isSiteAdmin, isOrgAdmin, isEventPlanner] = await Promise.all([
      this.authService.hasRoleAsync('SiteAdmin'),
      this.authService.hasRoleAsync('OrgAdmin'),
      this.authService.hasRoleAsync('EventPlanner')
    ]);

    window.setTimeout(() => {
      this.isSiteAdminUser = isSiteAdmin;

      const canSeeAllTeamsDataView = isSiteAdmin || isOrgAdmin || isEventPlanner;

      this.canManageTeamLifecycle = isSiteAdmin || isOrgAdmin || isEventPlanner;
      this.canRestoreDeletedTeams = isSiteAdmin;
      this.canHardDeleteTeams = isSiteAdmin;
      this.canManagePlayerLifecycle = isSiteAdmin || isOrgAdmin || isEventPlanner;
      this.canRestoreDeletedPlayers = isSiteAdmin;
      this.canHardDeletePlayers = isSiteAdmin;
      this.showOrganizationColumn.set(canSeeAllTeamsDataView);
      this.canResolveOrganizationLabelsById = isSiteAdmin || isOrgAdmin;
      if (canSeeAllTeamsDataView) {
        this.loadOrganizationLabels();
      }
    }, 0);
  }

  private loadOrganizationLabels(): void {
    const request$ = this.isSiteAdminUser
      ? this.organizationService.getAll()
      : this.organizationService.getMyOrganization().pipe(map(org => [org]));

    request$.subscribe({
      next: (organizations) => {
        this.applyOrganizationLabels(organizations);
      },
      error: () => {
        this.organizationService.getMyOrganization().subscribe({
          next: (org) => this.applyOrganizationLabels([org]),
          error: () => {
            // Keep fallback labels when org lookup is unavailable.
          }
        });
      }
    });
  }

  private applyOrganizationLabels(organizations: OrganizationDto[]): void {
    this.organizationNamesById.clear();
    for (const org of organizations) {
      this.organizationNamesById.set(org.id, org.displayName || org.legalName || `Org ${org.id}`);
    }
  }

  private ensureOrganizationLabelsFromTeams(teams: TeamRecord[]): void {
    const missingOrgIds = [...new Set(teams.map(t => t.organizationId))]
      .filter(id => id > 0 && !this.organizationNamesById.has(id));

    for (const orgId of missingOrgIds) {
      this.organizationService.getById(orgId).subscribe({
        next: (org) => {
          this.organizationNamesById.set(org.id, org.displayName || org.legalName || `Org ${org.id}`);
        },
        error: () => {
          // Keep fallback label format when org lookup is not available.
        }
      });
    }
  }

  private loadOrganizationContext(): void {
    this.organizationService.getMyOrganization().subscribe({
      next: (org) => {
        this.currentOrganizationId = org.id;
        this.organizationNamesById.set(org.id, org.displayName || org.legalName || `Org ${org.id}`);
      },
      error: (err) => {
        console.error('Failed to load current organization:', err);
      }
    });
  }

  private loadTeams(): void {
    this.teamService.getAll().subscribe({
      next: (dtos: TeamDto[]) => {
        const records = dtos.map(dto => this.dtoToRecord(dto));
        this.teams.set(records);
        if (this.canResolveOrganizationLabelsById) {
          this.ensureOrganizationLabelsFromTeams(records);
        }
        if (!this.currentOrganizationId && dtos.length > 0) {
          this.currentOrganizationId = dtos[0].organizationId;
        }
        this.refreshEventTeamAssignments();
        this.refreshAssignments();
      },
      error: (err) => {
        console.error('Failed to load teams:', err);
        this.teams.set([]);
        this.assignments.set([]);
      }
    });
  }

  private loadEvents(): void {
    this.eventService.getAll().subscribe({
      next: (dtos: EventDto[]) => {
        this.eventOptions = dtos.map(dto => ({ id: dto.id, name: dto.name }));
        if (!this.currentOrganizationId && dtos.length > 0) {
          this.currentOrganizationId = dtos[0].organizationId;
        }
        this.refreshEventTeamAssignments();
      },
      error: (err) => {
        console.error('Failed to load events:', err);
        this.eventOptions = [];
      }
    });
  }

  private dtoToRecord(dto: TeamDto): TeamRecord {
    return {
      id: dto.id,
      organizationId: dto.organizationId,
      name: dto.name,
      eventIds: [],
      color: dto.color,
      isEnabled: dto.isEnabled ?? true,
      isDeleted: dto.isDeleted ?? false
    };
  }

  private mapTeamPlayerMaintenanceDto(dto: TeamPlayerMaintenanceDto): PlayerRecord {
    return {
      id: dto.id,
      firstName: dto.firstName,
      lastName: dto.lastName,
      userId: dto.userId,
      order: dto.order,
      enableCustomSound: dto.enableCustomSound,
      enableCustomVideo: dto.enableCustomVideo,
      isEnabled: dto.isEnabled,
      isDeleted: dto.isDeleted,
      hasCustomSoundArtifact: dto.hasCustomSoundArtifact ?? false,
      hasCustomVideoArtifact: dto.hasCustomVideoArtifact ?? false
    };
  }

  private recordToCreateDto(record: TeamRecord): TeamCreateDto {
    const resolvedOrganizationId = this.currentOrganizationId;
    if (!resolvedOrganizationId || resolvedOrganizationId <= 0) {
      throw new Error('Organization context is missing for team creation.');
    }

    return {
      organizationId: resolvedOrganizationId,
      name: record.name,
      color: record.color
    };
  }

  private recordToUpdateDto(record: TeamRecord): TeamUpdateDto {
    return {
      name: record.name,
      color: record.color
    };
  }

  private refreshEventTeamAssignments(): void {
    const teams = this.teams();
    if (teams.length === 0) {
      return;
    }

    const requests = teams.map(team =>
      this.teamService.getEventTeamsByTeamId(team.id).pipe(
        map(assignments => ({ teamId: team.id, assignments })),
        catchError((err) => {
          console.error(`Failed to load event teams for team ${team.id}:`, err);
          return of({ teamId: team.id, assignments: [] as EventTeamAssignmentDto[] });
        })
      )
    );

    forkJoin(requests).subscribe(results => {
      const assignmentMap: Record<number, EventTeamAssignmentDto[]> = {};
      for (const result of results) {
        assignmentMap[result.teamId] = [...result.assignments];
      }

      this.eventTeamAssignmentsByTeamId = assignmentMap;
      this.teams.update(list => list.map(team => ({
        ...team,
        eventIds: (assignmentMap[team.id] ?? []).map(a => a.eventId)
      })));
    });
  }

  private persistEventAssignments(teamId: number, previousEventIds: number[], nextEventIds: number[], onComplete: () => void): void {
    const existingAssignments = this.eventTeamAssignmentsByTeamId[teamId] ?? [];
    const normalizedNextEventIds = [...new Set(nextEventIds.filter(id => id > 0))];
    const assignmentsToRemove = existingAssignments.filter(a => !normalizedNextEventIds.includes(a.eventId));
    const eventIdsToAdd = normalizedNextEventIds.filter(eventId => !existingAssignments.some(a => a.eventId === eventId));

    const removeRequests = assignmentsToRemove.map(a => this.teamService.removeEventTeam(a.id));
    const removeAll$ = removeRequests.length > 0 ? forkJoin(removeRequests) : of([]);

    removeAll$.subscribe({
      next: () => {
        this.eventTeamAssignmentsByTeamId[teamId] = existingAssignments.filter(a => normalizedNextEventIds.includes(a.eventId));
        this.addEventAssignmentsSequentially(teamId, previousEventIds, eventIdsToAdd, 0, onComplete);
      },
      error: (err) => {
        console.error('Failed to remove existing team event assignments:', err);
        this.teams.update(list => list.map(team => team.id === teamId ? { ...team, eventIds: previousEventIds } : team));
      }
    });
  }

  private addEventAssignmentsSequentially(teamId: number, previousEventIds: number[], eventIdsToAdd: number[], index: number, onComplete: () => void): void {
    if (index >= eventIdsToAdd.length) {
      this.teams.update(list => list.map(team => team.id === teamId ? {
        ...team,
        eventIds: (this.eventTeamAssignmentsByTeamId[teamId] ?? []).map(a => a.eventId)
      } : team));
      onComplete();
      return;
    }

    const eventId = eventIdsToAdd[index];
    this.teamService.assignTeamToEvent({ eventId, teamId }).subscribe({
      next: (assignment) => {
        const current = this.eventTeamAssignmentsByTeamId[teamId] ?? [];
        this.eventTeamAssignmentsByTeamId[teamId] = [...current, assignment];
        this.addEventAssignmentsSequentially(teamId, previousEventIds, eventIdsToAdd, index + 1, onComplete);
      },
      error: (err) => {
        console.error('Failed to assign team to event:', err);
        this.teams.update(list => list.map(team => team.id === teamId ? { ...team, eventIds: previousEventIds } : team));
      }
    });
  }

  getEventNames(eventIds: number[]): string {
    if (!eventIds || eventIds.length === 0) return 'Unassigned';
    const names = eventIds
      .map(eventId => this.eventOptions.find(e => e.id === eventId)?.name)
      .filter((name): name is string => !!name);
    if (names.length > 0) {
      return names.join(', ');
    }

    return eventIds.map(eventId => `Event ${eventId}`).join(', ');
  }

  getOrganizationName(organizationId: number): string {
    return this.organizationNamesById.get(organizationId) ?? `Org ${organizationId}`;
  }

  selectTeam(team: TeamRecord): void {
    this.selectedTeam = this.selectedTeam?.id === team.id ? null : team;
  }

  openNew(): void {
    this.isNewTeam  = true;
    this.editDraft  = { id: 0, organizationId: this.currentOrganizationId ?? 0, name: '', eventIds: [], color: 'blue', isEnabled: true, isDeleted: false };
    this.pendingTeamLifecycleAction = 'none';
    this.isModalOpen = true;
  }

  openEdit(team: TeamRecord): void {
    this.isNewTeam  = false;
    this.editDraft  = { ...team, eventIds: [...team.eventIds] };
    this.pendingTeamLifecycleAction = 'none';
    this.isModalOpen = true;
  }

  closeModal(): void {
    this.isModalOpen = false;
    this.pendingTeamLifecycleAction = 'none';
  }

  selectTeamLifecycleAction(action: TeamLifecycleAction): void {
    if (action === 'none') {
      this.pendingTeamLifecycleAction = 'none';
      return;
    }

    this.pendingTeamLifecycleAction = this.pendingTeamLifecycleAction === action ? 'none' : action;
  }

  isTeamLifecycleActionSelected(action: TeamLifecycleAction): boolean {
    return this.pendingTeamLifecycleAction === action;
  }

  saveTeam(): void {
    if (!this.editDraft.name.trim()) return;

    if (!this.isNewTeam && this.pendingTeamLifecycleAction === 'delete') {
      const confirmed = window.confirm('Delete this team on OK confirmation? This performs a soft delete and disables the team.');
      if (!confirmed) {
        return;
      }
    }

    if (this.isNewTeam) {
      const createDto = this.recordToCreateDto(this.editDraft);
      this.teamService.create(createDto).subscribe({
        next: (dto) => {
          const newRecord = { ...this.dtoToRecord(dto), eventIds: [...this.normalizeEventIds(this.editDraft.eventIds)] };
          this.teams.update(list => [...list, newRecord]);
          this.persistEventAssignments(newRecord.id, [], this.editDraft.eventIds, () => {
            this.refreshAssignments();
            this.closeModal();
          });
        },
        error: (err) => console.error('Failed to create team:', err)
      });
    } else {
      const previousEventIds = this.selectedTeam?.eventIds ?? [];
      const updateDto = this.recordToUpdateDto(this.editDraft);
      this.teamService.update(this.editDraft.id, updateDto).subscribe({
        next: (dto) => {
          const updatedRecord = { ...this.dtoToRecord(dto), eventIds: [...this.normalizeEventIds(this.editDraft.eventIds)] };
          this.teams.update(list =>
            list.map(t => t.id === updatedRecord.id ? updatedRecord : t)
          );
          this.persistEventAssignments(updatedRecord.id, previousEventIds, this.editDraft.eventIds, () => {
            this.executePendingTeamLifecycleAction(() => {
              this.refreshAssignments();
              this.closeModal();
            });
          });
        },
        error: (err) => console.error('Failed to update team:', err)
      });
    }
  }

  private executePendingTeamLifecycleAction(onComplete: () => void): void {
    if (this.pendingTeamLifecycleAction === 'none') {
      onComplete();
      return;
    }

    if (this.pendingTeamLifecycleAction === 'disable') {
      this.teamService.setEnabled(this.editDraft.id, false).subscribe({
        next: (dto) => {
          const updated = this.dtoToRecord(dto);
          this.editDraft = { ...updated, eventIds: [...this.editDraft.eventIds] };
          this.teams.update(list => list.map(t => t.id === updated.id ? { ...t, isEnabled: updated.isEnabled, isDeleted: updated.isDeleted } : t));
          onComplete();
        },
        error: (err) => console.error('Failed to disable team:', err)
      });
      return;
    }

    if (this.pendingTeamLifecycleAction === 'restore') {
      const restoreRequest = this.editDraft.isDeleted
        ? this.teamService.restore(this.editDraft.id)
        : this.teamService.setEnabled(this.editDraft.id, true);

      restoreRequest.subscribe({
        next: (dto) => {
          const updated = this.dtoToRecord(dto);
          this.editDraft = { ...updated, eventIds: [...this.editDraft.eventIds] };
          this.teams.update(list => list.map(t => t.id === updated.id ? { ...t, isEnabled: updated.isEnabled, isDeleted: updated.isDeleted } : t));
          onComplete();
        },
        error: (err) => console.error('Failed to restore team:', err)
      });
      return;
    }

    this.teamService.delete(this.editDraft.id).subscribe({
      next: () => {
        this.editDraft = { ...this.editDraft, isDeleted: true, isEnabled: false };
        this.teams.update(list =>
          list.map(t => t.id === this.editDraft.id ? { ...t, isDeleted: true, isEnabled: false } : t)
        );
        onComplete();
      },
      error: (err) => console.error('Failed to delete team:', err)
    });
  }

  deleteTeam(): void {
    if (!window.confirm('Soft delete this team and disable it?')) {
      return;
    }

    this.teamService.delete(this.editDraft.id).subscribe({
      next: () => {
        this.editDraft = { ...this.editDraft, isDeleted: true, isEnabled: false };
        this.teams.update(list =>
          list.map(t => t.id === this.editDraft.id ? { ...t, isDeleted: true, isEnabled: false } : t)
        );
        this.refreshAssignments();
      },
      error: (err) => console.error('Failed to delete team:', err)
    });
  }

  toggleTeamEnabled(): void {
    this.teamService.setEnabled(this.editDraft.id, !this.editDraft.isEnabled).subscribe({
      next: (dto) => {
        const updated = this.dtoToRecord(dto);
        this.editDraft = { ...updated, eventIds: [...this.editDraft.eventIds] };
        this.teams.update(list => list.map(t => t.id === updated.id ? { ...t, isEnabled: updated.isEnabled, isDeleted: updated.isDeleted } : t));
        this.refreshAssignments();
      },
      error: (err) => console.error('Failed to toggle team enabled state:', err)
    });
  }

  restoreTeam(): void {
    this.teamService.restore(this.editDraft.id).subscribe({
      next: (dto) => {
        const updated = this.dtoToRecord(dto);
        this.editDraft = { ...updated, eventIds: [...this.editDraft.eventIds] };
        this.teams.update(list => list.map(t => t.id === updated.id ? { ...t, isEnabled: updated.isEnabled, isDeleted: updated.isDeleted } : t));
        this.refreshAssignments();
      },
      error: (err) => console.error('Failed to restore team:', err)
    });
  }

  hardDeleteTeam(): void {
    if (!window.confirm('Permanently delete this team and related team-specific player media? This cannot be undone.')) {
      return;
    }

    this.teamService.hardDelete(this.editDraft.id).subscribe({
      next: () => {
        delete this.eventTeamAssignmentsByTeamId[this.editDraft.id];
        this.teams.update(list => list.filter(t => t.id !== this.editDraft.id));
        if (this.selectedTeam?.id === this.editDraft.id) {
          this.selectedTeam = null;
          this.players.set([]);
        }
        this.refreshAssignments();
        this.closeModal();
      },
      error: (err) => console.error('Failed to hard delete team:', err)
    });
  }

  private mapTeamPlayerDto(dto: TeamPlayerDto): PlayerRecord {
    return {
      id: dto.id,
      firstName: dto.firstName,
      lastName: dto.lastName,
      userId: dto.userId,
      order: dto.order,
      enableCustomSound: dto.enableCustomSound,
      enableCustomVideo: dto.enableCustomVideo,
      isEnabled: dto.isEnabled ?? true,
      isDeleted: dto.isDeleted ?? false,
      hasCustomSoundArtifact: dto.hasCustomSoundArtifact,
      hasCustomVideoArtifact: dto.hasCustomVideoArtifact
    };
  }

  private mapAssignmentDto(dto: TeamAssignmentDto): PlayerAssignment {
    return {
      id: dto.id,
      playerId: dto.playerId,
      teamId: dto.teamId,
      order: dto.order,
      firstName: dto.firstName,
      lastName: dto.lastName,
      userId: dto.userId,
      enableCustomSound: dto.enableCustomSound,
      enableCustomVideo: dto.enableCustomVideo,
      isEnabled: true,
      isDeleted: false,
      hasCustomSoundArtifact: dto.hasCustomSoundArtifact,
      hasCustomVideoArtifact: dto.hasCustomVideoArtifact
    };
  }

  private getPlayerIdentityKey(player: { userId?: number; firstName: string; lastName: string }): string {
    if (player.userId) {
      return `user:${player.userId}`;
    }

    const first = player.firstName.trim().toLowerCase();
    const last = player.lastName.trim().toLowerCase();
    return `name:${first}|${last}`;
  }

  private refreshAssignments(): void {
    const teamIds = this.teams().map(t => t.id);
    const assignmentRequests = teamIds.map(teamId =>
      this.teamService.getAssignmentsByTeamId(teamId).pipe(
        map((dtos) => dtos.map(dto => this.mapAssignmentDto(dto))),
        catchError((err) => {
          console.error(`Failed to load assignments for team ${teamId}:`, err);
          return of([] as PlayerAssignment[]);
        })
      )
    );

    const assignments$ = assignmentRequests.length > 0
      ? forkJoin(assignmentRequests)
      : of([] as PlayerAssignment[][]);

    const persistedPlayers$ = this.teamService.getAllPlayersForMaintenance().pipe(
      map((dtos) => dtos
        .filter(dto => dto.isEnabled && !dto.isDeleted)
        .map(dto => this.mapTeamPlayerMaintenanceDto(dto))),
      catchError((err) => {
        console.error('Failed to load persisted players:', err);
        return of([] as PlayerRecord[]);
      })
    );

    forkJoin({ resultSets: assignments$, persistedPlayers: persistedPlayers$ }).subscribe(({ resultSets, persistedPlayers }) => {
      const flatAssignments = resultSets.flat();
      this.assignments.set(flatAssignments);

      const uniquePersistedPlayers = new Map<number, PlayerRecord>();
      for (const persistedPlayer of persistedPlayers) {
        uniquePersistedPlayers.set(persistedPlayer.id, persistedPlayer);
      }

      for (const a of flatAssignments) {
        const candidate: PlayerRecord = {
          id: a.playerId,
          firstName: a.firstName,
          lastName: a.lastName,
          userId: a.userId,
          order: a.order,
          enableCustomSound: a.enableCustomSound,
          enableCustomVideo: a.enableCustomVideo,
          isEnabled: a.isEnabled ?? true,
          isDeleted: a.isDeleted ?? false,
          hasCustomSoundArtifact: a.hasCustomSoundArtifact,
          hasCustomVideoArtifact: a.hasCustomVideoArtifact
        };

        uniquePersistedPlayers.set(candidate.id, candidate);
      }

      const mergedPlayers = [...uniquePersistedPlayers.values(), ...this.localPlayers()];
      this.players.set(mergedPlayers);
    });
  }

  private loadPlayersByTeam(teamId: number): void {
    this.teamService.getPlayersByTeamId(teamId).subscribe({
      next: (dtos) => {
        const mapped = dtos.map(d => this.mapTeamPlayerDto(d));
        this.players.set(mapped);
        this.assignmentSourcePlayerId = 0;
        this.assignmentTargetTeamId = 0;
      },
      error: (err) => {
        console.error('Failed to load team players:', err);
        this.players.set([]);
      }
    });
  }

  getTeamName(teamId: number): boolean | string {
    return this.teams().find(t => t.id === teamId)?.name ?? '—';
  }

  isOrgLinked(player: { userId?: number }): boolean {
    return typeof player.userId === 'number' && player.userId > 0;
  }

  openPlayerMedia(player: { userId?: number }): void {
    if (!this.isOrgLinked(player)) {
      return;
    }

    void this.router.navigate(['/my-media'], {
      queryParams: { selectedUserId: player.userId }
    });
  }

  // ── Player helpers ─────────────────────────────────────────────────────
  addPlayer(): void {
    const localId = -Date.now();
    const localPlayer: PlayerRecord = {
      id: localId,
      firstName: '',
      lastName: '',
      order: this.players().length + 1,
      enableCustomSound: false,
      enableCustomVideo: false,
      isEnabled: true,
      isDeleted: false
    };

    this.localPlayers.update(list => [...list, localPlayer]);
    this.players.update(list => [...list, localPlayer]);
  }

  savePlayer(player: PlayerRecord): void {
    const firstName = player.firstName?.trim();
    const lastName = player.lastName?.trim();
    if (!firstName || !lastName) return;

    if (player.id < 0) {
      const resolvedTeamId = this.resolvePlayerCreateTeamId();
      if (!resolvedTeamId) {
        console.error('Unable to create player because no team context is available.');
        return;
      }

      const createDto: TeamPlayerCreateDto = {
        teamId: resolvedTeamId,
        userId: player.userId,
        firstName,
        lastName,
        order: Math.max(1, player.order),
        enableCustomSound: player.enableCustomSound,
        enableCustomVideo: player.enableCustomVideo
      };

      this.teamService.createPlayer(createDto).subscribe({
        next: (dto) => {
          const created = this.mapTeamPlayerDto(dto);
          this.localPlayers.update(list => list.filter(p => p.id !== player.id));
          this.players.update(list => [...list.filter(p => p.id !== player.id), created]);
          this.refreshAssignments();
        },
        error: (err) => console.error('Failed to create player:', err)
      });

      return;
    }

    const updateDto: TeamPlayerUpdateDto = {
      userId: player.userId,
      firstName,
      lastName,
      order: player.order,
      enableCustomSound: player.enableCustomSound,
      enableCustomVideo: player.enableCustomVideo
    };

    this.teamService.updatePlayer(player.id, updateDto).subscribe({
      next: (dto) => {
        const updated = this.mapTeamPlayerDto(dto);
        this.players.update(list => list.map(p => p.id === updated.id ? updated : p));
        this.refreshAssignments();
      },
      error: (err) => console.error('Failed to save player:', err)
    });
  }

  savePlayerCustomMedia(player: PlayerRecord): void {
    if (player.id < 0) {
      this.localPlayers.update(list =>
        list.map(p => p.id === player.id
          ? { ...p, enableCustomSound: player.enableCustomSound, enableCustomVideo: player.enableCustomVideo }
          : p)
      );
      this.players.update(list =>
        list.map(p => p.id === player.id
          ? { ...p, enableCustomSound: player.enableCustomSound, enableCustomVideo: player.enableCustomVideo }
          : p)
      );
      return;
    }

    const updateDto: TeamPlayerUpdateDto = {
      userId: player.userId,
      firstName: player.firstName,
      lastName: player.lastName,
      order: player.order,
      enableCustomSound: player.enableCustomSound,
      enableCustomVideo: player.enableCustomVideo
    };

    this.teamService.updatePlayer(player.id, updateDto).subscribe({
      next: (dto) => {
        const updated = this.mapTeamPlayerDto(dto);
        this.players.update(list => list.map(p => p.id === updated.id ? updated : p));
        this.refreshAssignments();
      },
      error: (err) => console.error('Failed to update player custom media flags:', err)
    });
  }

  togglePlayerCustomMedia(player: PlayerRecord, mediaType: 'sound' | 'video'): void {
    if (!player.userId) {
      return;
    }

    if (mediaType === 'sound' && !player.hasCustomSoundArtifact) {
      return;
    }

    if (mediaType === 'video' && !player.hasCustomVideoArtifact) {
      return;
    }

    const updated: PlayerRecord = {
      ...player,
      enableCustomSound: mediaType === 'sound' ? !player.enableCustomSound : player.enableCustomSound,
      enableCustomVideo: mediaType === 'video' ? !player.enableCustomVideo : player.enableCustomVideo
    };

    this.players.update(list => list.map(p => p.id === updated.id ? updated : p));
    this.savePlayerCustomMedia(updated);
  }

  revertPlayerToManual(player: PlayerRecord): void {
    const updated: PlayerRecord = {
      ...player,
      userId: undefined,
      enableCustomSound: false,
      enableCustomVideo: false
    };

    if (updated.id < 0) {
      this.localPlayers.update(list => list.map(p => p.id === updated.id ? updated : p));
      this.players.update(list => list.map(p => p.id === updated.id ? updated : p));
      return;
    }

    this.players.update(list => list.map(p => p.id === updated.id ? updated : p));
    this.savePlayer(updated);
  }

  onPlayerDeleteClicked(player: PlayerRecord): void {
    if (player.id < 0) {
      this.localPlayers.update(list => list.filter(p => p.id !== player.id));
      this.players.update(list => list.filter(p => p.id !== player.id));
      return;
    }

    this.playerModalDraft = { ...player };
    this.pendingPlayerLifecycleAction = 'none';
    this.isPlayerModalOpen = true;
  }

  closePlayerModal(): void {
    this.isPlayerModalOpen = false;
    this.playerModalDraft = null;
    this.pendingPlayerLifecycleAction = 'none';
  }

  selectPlayerLifecycleAction(action: PlayerLifecycleAction): void {
    if (action === 'none') {
      this.pendingPlayerLifecycleAction = 'none';
      return;
    }

    this.pendingPlayerLifecycleAction = this.pendingPlayerLifecycleAction === action ? 'none' : action;
  }

  isPlayerLifecycleActionSelected(action: PlayerLifecycleAction): boolean {
    return this.pendingPlayerLifecycleAction === action;
  }

  confirmPlayerModalLifecycleAction(): void {
    if (!this.playerModalDraft) {
      return;
    }

    if (this.pendingPlayerLifecycleAction === 'none') {
      return;
    }

    if (this.pendingPlayerLifecycleAction === 'delete') {
      const confirmed = window.confirm('Delete this player on OK confirmation? This performs a soft delete and disables the player.');
      if (!confirmed) {
        return;
      }
    }

    this.executePendingPlayerLifecycleAction();
  }

  private executePendingPlayerLifecycleAction(): void {
    if (!this.playerModalDraft) {
      return;
    }

    if (this.pendingPlayerLifecycleAction === 'disable') {
      this.teamService.setPlayerEnabled(this.playerModalDraft.id, false).subscribe({
        next: (dto) => {
          const updated = this.mapTeamPlayerDto(dto);
          this.players.update(list => list.map(p => p.id === updated.id ? updated : p));
          this.refreshAssignments();
          this.closePlayerModal();
        },
        error: (err) => console.error('Failed to disable player:', err)
      });
      return;
    }

    if (this.pendingPlayerLifecycleAction === 'restore') {
      const restoreRequest = this.playerModalDraft.isDeleted
        ? this.teamService.restorePlayer(this.playerModalDraft.id)
        : this.teamService.setPlayerEnabled(this.playerModalDraft.id, true);

      restoreRequest.subscribe({
        next: (dto) => {
          const updated = this.mapTeamPlayerDto(dto);
          this.players.update(list => list.map(p => p.id === updated.id ? updated : p));
          this.refreshAssignments();
          this.closePlayerModal();
        },
        error: (err) => console.error('Failed to restore player:', err)
      });
      return;
    }

    this.teamService.deletePlayer(this.playerModalDraft.id).subscribe({
      next: () => {
        if (!this.playerModalDraft) {
          return;
        }

        this.players.update(list => list.filter(p => p.id !== this.playerModalDraft!.id));
        this.refreshAssignments();
        this.closePlayerModal();
      },
      error: (err) => console.error('Failed to delete player:', err)
    });
  }

  addAssignment(): void {
    if (this.assignmentSourcePlayerId === 0 || this.assignmentTargetTeamId === 0) return;

    const player = this.players().find(p => p.id === this.assignmentSourcePlayerId);
    if (!player) return;

    const firstName = player.firstName.trim();
    const lastName = player.lastName.trim();
    if (!firstName || !lastName) return;

    const createAssignmentForPlayer = (playerId: number): void => {
      this.teamService.createAssignment({
        teamId: this.assignmentTargetTeamId,
        playerId
      }).subscribe({
        next: () => {
          if (player.id < 0) {
            this.localPlayers.update(list => list.filter(p => p.id !== player.id));
            this.players.update(list => list.filter(p => p.id !== player.id));
          }

          this.refreshAssignments();
          this.assignmentSourcePlayerId = 0;
          this.assignmentTargetTeamId = 0;
        },
        error: (err) => console.error('Failed to create team assignment:', err)
      });
    };

    if (player.id < 0) {
      const createPlayerDto: TeamPlayerCreateDto = {
        teamId: this.assignmentTargetTeamId,
        userId: player.userId && player.userId > 0 ? player.userId : undefined,
        firstName,
        lastName,
        order: 0,
        enableCustomSound: player.enableCustomSound,
        enableCustomVideo: player.enableCustomVideo
      };

      this.teamService.createPlayer(createPlayerDto).subscribe({
        next: createdPlayer => createAssignmentForPlayer(createdPlayer.id),
        error: (err) => console.error('Failed to create player before assignment:', err)
      });
      return;
    }

    createAssignmentForPlayer(player.id);
  }

  saveAssignmentOrder(assignment: PlayerAssignment): void {
    const existing = this.assignments().find(a => a.id === assignment.id);
    if (!existing || assignment.order < 1) return;

    const updateDto: TeamPlayerUpdateDto = {
      userId: existing.userId,
      firstName: existing.firstName,
      lastName: existing.lastName,
      order: assignment.order,
      enableCustomSound: existing.enableCustomSound,
      enableCustomVideo: existing.enableCustomVideo
    };

    this.teamService.updatePlayer(assignment.id, updateDto).subscribe({
      next: () => {
        this.assignments.update(list => list.map(a => a.id === assignment.id ? { ...a, order: assignment.order } : a));
        this.refreshAssignments();
      },
      error: (err) => console.error('Failed to update assignment order:', err)
    });
  }

  deleteAssignment(assignmentId: number): void {
    this.teamService.deleteAssignment(assignmentId).subscribe({
      next: () => {
        this.assignments.update(list => list.filter(a => a.id !== assignmentId));
        this.refreshAssignments();
      },
      error: (err) => console.error('Failed to delete assignment:', err)
    });
  }

  onAssignmentDragStart(event: DragEvent, assignmentId: number): void {
    if (this.isAssignmentReorderSaving) {
      event.preventDefault();
      return;
    }

    this.draggingAssignmentId = assignmentId;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', String(assignmentId));
    }
  }

  onAssignmentDragOver(event: DragEvent, assignmentId: number): void {
    if (!this.canDropAssignmentOnTarget(assignmentId)) {
      return;
    }

    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    this.dragOverAssignmentId = assignmentId;
  }

  onAssignmentDragLeave(event: DragEvent): void {
    const relatedTarget = event.relatedTarget as Node | null;
    if (!relatedTarget || !(event.currentTarget as Element).contains(relatedTarget)) {
      this.dragOverAssignmentId = null;
    }
  }

  onAssignmentDrop(event: DragEvent, targetAssignmentId: number): void {
    event.preventDefault();
    this.dragOverAssignmentId = null;

    if (!this.canDropAssignmentOnTarget(targetAssignmentId)) {
      this.draggingAssignmentId = null;
      return;
    }

    const source = this.assignments().find(a => a.id === this.draggingAssignmentId);
    const target = this.assignments().find(a => a.id === targetAssignmentId);
    if (!source || !target || source.teamId !== target.teamId) {
      this.draggingAssignmentId = null;
      return;
    }

    const teamAssignments = this.assignments()
      .filter(a => a.teamId === source.teamId)
      .sort((a, b) => a.order - b.order);

    const sourceIndex = teamAssignments.findIndex(a => a.id === source.id);
    const targetIndex = teamAssignments.findIndex(a => a.id === target.id);
    if (sourceIndex < 0 || targetIndex < 0) {
      this.draggingAssignmentId = null;
      return;
    }

    const reordered = [...teamAssignments];
    const [moved] = reordered.splice(sourceIndex, 1);
    reordered.splice(targetIndex, 0, moved);

    const updatedForTeam = reordered.map((item, index) => ({ ...item, order: index + 1 }));
    const updatedById = new Map(updatedForTeam.map(item => [item.id, item]));

    const hasChanges = updatedForTeam.some(updated => {
      const current = teamAssignments.find(item => item.id === updated.id);
      return !!current && current.order !== updated.order;
    });

    if (!hasChanges) {
      this.draggingAssignmentId = null;
      return;
    }

    this.assignments.update(list =>
      list.map(item => updatedById.get(item.id) ?? item)
    );

    this.isAssignmentReorderSaving = true;
    this.persistAssignmentOrder(updatedForTeam);
    this.onAssignmentDragEnd();
  }

  onAssignmentDragEnd(): void {
    this.draggingAssignmentId = null;
    this.dragOverAssignmentId = null;
  }

  private canDropAssignmentOnTarget(targetAssignmentId: number): boolean {
    if (this.isAssignmentReorderSaving || !this.draggingAssignmentId || this.draggingAssignmentId === targetAssignmentId) {
      return false;
    }

    const source = this.assignments().find(a => a.id === this.draggingAssignmentId);
    const target = this.assignments().find(a => a.id === targetAssignmentId);
    return !!source && !!target && source.teamId === target.teamId;
  }

  openOrgUserPicker(player: PlayerRecord): void {
    this.activePlayerForOrgUser = player;
    this.orgUserFilterText = '';
    this.isOrgUserPickerOpen = true;

    this.orgUsersLoading = true;

    if (this.isSiteAdminUser) {
      this.userManagementService.getManagedUsers().subscribe({
        next: users => {
          this.orgUsers = users;
          this.orgUsersLoading = false;
        },
        error: (err) => {
          console.error('Failed to load managed users for picker:', err);
          this.orgUsers = [];
          this.orgUsersLoading = false;
        }
      });

      return;
    }

    const organizationId = this.resolvePickerOrganizationId(player);
    if (!organizationId || organizationId <= 0) {
      this.orgUsers = [];
      this.orgUsersLoading = false;
      return;
    }

    this.userManagementService.getManagedUsersByOrganization(organizationId).subscribe({
      next: users => {
        this.orgUsers = users;
        this.orgUsersLoading = false;
      },
      error: (err) => {
        console.error('Failed to load organization users for picker:', err);
        this.orgUsers = [];
        this.orgUsersLoading = false;
      }
    });
  }

  closeOrgUserPicker(): void {
    this.isOrgUserPickerOpen = false;
    this.orgUsersLoading = false;
    this.orgUserFilterText = '';
    this.orgUsers = [];
    this.activePlayerForOrgUser = null;
  }

  selectOrgUserForPlayer(user: ManagedUserListItemDto): void {
    const target = this.activePlayerForOrgUser;
    if (!target) {
      return;
    }

    const firstName = (user.firstName ?? '').trim();
    const lastName = (user.lastName ?? '').trim();
    const fullNameFallback = user.email?.split('@')[0] ?? `User ${user.userId}`;

    const updated: PlayerRecord = {
      ...target,
      userId: user.userId,
      firstName: firstName || target.firstName || fullNameFallback,
      lastName: lastName || target.lastName,
      hasCustomSoundArtifact: false,
      hasCustomVideoArtifact: false,
      enableCustomSound: false,
      enableCustomVideo: false
    };

    if (updated.id < 0) {
      this.localPlayers.update(list => list.map(p => p.id === updated.id ? updated : p));
      this.players.update(list => list.map(p => p.id === updated.id ? updated : p));
      this.closeOrgUserPicker();
      return;
    }

    this.players.update(list => list.map(p => p.id === updated.id ? updated : p));
    this.savePlayer(updated);
    this.closeOrgUserPicker();
  }

  getManagedUserDisplayName(user: ManagedUserListItemDto): string {
    const full = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    return full || user.email || `User ${user.userId}`;
  }

  private resolvePickerOrganizationId(player: PlayerRecord): number | null {
    if (this.selectedTeam?.organizationId) {
      return this.selectedTeam.organizationId;
    }

    const assignment = this.assignments().find(a => a.playerId === player.id);
    if (assignment) {
      const team = this.teams().find(t => t.id === assignment.teamId);
      if (team?.organizationId) {
        return team.organizationId;
      }
    }

    return this.currentOrganizationId;
  }

  private resolvePlayerCreateTeamId(): number | null {
    if (this.selectedTeam?.id) {
      return this.selectedTeam.id;
    }

    if (this.assignmentTargetTeamId > 0) {
      return this.assignmentTargetTeamId;
    }

    const fallbackTeamId = this.teams().find(team => team.isEnabled && !team.isDeleted)?.id
      ?? this.teams()[0]?.id;
    return fallbackTeamId ?? null;
  }

  onEditEventSelectionChanged(selection: number[] | number): void {
    const values = Array.isArray(selection) ? selection.map(Number) : [Number(selection)];
    this.editDraft.eventIds = values.includes(0) ? [] : this.normalizeEventIds(values);
  }

  private normalizeEventIds(eventIds: number[] | undefined): number[] {
    return [...new Set((eventIds ?? []).map(Number).filter(id => Number.isFinite(id) && id > 0))];
  }

  private persistAssignmentOrder(items: PlayerAssignment[]): void {
    if (items.length === 0) {
      this.isAssignmentReorderSaving = false;
      return;
    }

    const teamId = items[0].teamId;
    const assignmentIds = items.map(item => item.id);
    this.teamService.reorderAssignments(teamId, { assignmentIds }).subscribe({
      next: () => {
        this.isAssignmentReorderSaving = false;
        this.refreshAssignments();
      },
      error: (err) => {
        console.error('Failed to persist drag-drop assignment order:', err);
        this.isAssignmentReorderSaving = false;
        this.refreshAssignments();
      }
    });
  }
}
